import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Grid,
  Typography,
  Button,
  Paper,
  Chip,
  Alert,
  Box,
  CircularProgress,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Link,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import { TableVirtuoso } from 'react-virtuoso';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getSPDRosterDuesDetail,
  updateSPDRosterForm,
} from '../../../endpoints/rosterDues';
import {
  LocalChapterFormInfo,
  RosterDuesDetail,
} from '../../../types/rosterDues';
import {
  local_chapter_roster_dues_statuses,
  spd_roster_dues_statuses,
} from '../../../utils/variables';
import UserSearchModal from '../../../Components/UserSearchModal';
import AlertItem from '../../../Components/AlertItem';

const COMPLETED_STATUS_ID = '1d6ebbdb98334816b6ace293463d9069';
const SPD_COMPLETED_STATUS_ID = '612a5ef7baf940eea81f3d5b232c112f';

const RosterDuesChapterList: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentUserId = localStorage.getItem('user_id');

  const [detail, setDetail] = useState<RosterDuesDetail | null>(null);
  const [chapters, setChapters] = useState<LocalChapterFormInfo[]>([]);
  const [search, setSearch] = useState<string>('');
  const [delaySearch, setDelaySearch] = useState<string>('');
  const [expandedStatuses, setExpandedStatuses] = useState<Set<string>>(
    new Set()
  );
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState<string>('');

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editingStatusId, setEditingStatusId] = useState<string>('');
  const [editingAssigneeId, setEditingAssigneeId] = useState<string>('');
  const [editingAssigneeName, setEditingAssigneeName] = useState<string>('');
  const [editingApprovers, setEditingApprovers] = useState<
    Record<string, string>
  >({});
  const [editingApproverNames, setEditingApproverNames] = useState<
    Record<string, string>
  >({});
  const [assigneeModalOpen, setAssigneeModalOpen] = useState(false);
  const [approverModalOpen, setApproverModalOpen] = useState(false);
  const [editingApproverStatusId, setEditingApproverStatusId] =
    useState<string>('');
  const [alertState, setAlertState] = useState<{
    severity: 'success' | 'error' | 'warning' | 'info';
    description: string;
    title?: string;
  } | null>(null);

  const { data, isLoading, refetch } = useApiGet(
    ['spd-roster-dues-detail', id],
    () => getSPDRosterDuesDetail(id!),
    {
      enabled: !!id,
      retry: 1,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    }
  );

  useEffect(() => {
    // Clear state when id changes
    setDetail(null);
    setChapters([]);
    setSearch('');
    setDelaySearch('');
    setExpandedStatuses(new Set());
    setVerificationError('');
    setIsEditing(false);
    setEditingStatusId('');
    setEditingAssigneeId('');
    setEditingAssigneeName('');
    setEditingApprovers({});
    setEditingApproverNames({});
    setAlertState(null);
  }, [id]);

  // Refetch data on mount to ensure fresh data
  useEffect(() => {
    if (id) {
      refetch();
    }
  }, [id, refetch]);

  useEffect(() => {
    if (data) {
      setDetail(data);
      if (data.local_chapter_forms) {
        setChapters(data.local_chapter_forms);
      }
      // Initialize editing state with current values (only if not currently editing)
      if (data.can_edit && !isEditing) {
        setEditingStatusId(data.status_id);
        setEditingAssigneeId(data.assigned_to_id);
        setEditingAssigneeName(data.assigned_to);
        // Initialize approvers map
        const approversMap: Record<string, string> = {};
        const approverNamesMap: Record<string, string> = {};
        data.approvers.forEach((approver) => {
          approversMap[approver.status_id] = approver.user_id;
          approverNamesMap[approver.status_id] = approver.user_name;
        });
        setEditingApprovers(approversMap);
        setEditingApproverNames(approverNamesMap);
      }
    }
  }, [data, isEditing]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearch(delaySearch);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [delaySearch]);

  // Group chapters by status
  const groupedChapters = local_chapter_roster_dues_statuses.map((status) => {
    const statusChapters = chapters.filter((c) => c.status_id === status.id);
    // Filter by search if provided
    const filteredChapters =
      search.length > 0
        ? statusChapters.filter((c) =>
            c.local_chapter_name.toLowerCase().includes(search.toLowerCase())
          )
        : statusChapters;

    return {
      status,
      chapters: filteredChapters,
      isExpanded: expandedStatuses.has(status.id),
    };
  });

  const completedChapters = chapters.filter(
    (c) => c.status_id === COMPLETED_STATUS_ID
  );
  const allCompleted =
    chapters.length > 0 && completedChapters.length === chapters.length;

  // Only allow editing if detail is loaded and user is assigned
  const canEdit = detail !== null && detail.assigned_to_id === currentUserId;
  // Check if user has edit permission (different from canEdit which is for member status changes)
  const hasEditPermission = detail?.can_edit === true;

  const handleContinueToSummary = async () => {
    if (!id || !allCompleted) return;

    // Verify by calling the details endpoint before allowing navigation
    setIsVerifying(true);
    setVerificationError('');
    try {
      const verificationData = await getSPDRosterDuesDetail(id);
      if (verificationData && verificationData.local_chapter_forms) {
        const completedCount = verificationData.local_chapter_forms.filter(
          (c) => c.status_id === COMPLETED_STATUS_ID
        ).length;

        if (completedCount === verificationData.local_chapter_forms.length) {
          // All chapters are completed, allow navigation
          navigate(`/annual-reports/roster-dues/${id}/spd-summary`);
        } else {
          // Still have incomplete chapters, update the local state and show warning
          setChapters(verificationData.local_chapter_forms);
          setVerificationError(
            'Some chapters are not yet completed. Please ensure all chapters are completed before continuing.'
          );
        }
      }
    } catch (error) {
      console.error('Failed to verify chapter statuses:', error);
      setVerificationError(
        'Failed to verify chapter statuses. Please try again.'
      );
    } finally {
      setIsVerifying(false);
    }
  };

  const handleToggleStatus = (statusId: string) => {
    setExpandedStatuses((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(statusId)) {
        newSet.delete(statusId);
      } else {
        newSet.add(statusId);
      }
      return newSet;
    });
  };

  // Edit mode handlers
  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    // Reset to original values
    if (detail) {
      setEditingStatusId(detail.status_id);
      setEditingAssigneeId(detail.assigned_to_id);
      setEditingAssigneeName(detail.assigned_to);
      const approversMap: Record<string, string> = {};
      const approverNamesMap: Record<string, string> = {};
      detail.approvers.forEach((approver) => {
        approversMap[approver.status_id] = approver.user_id;
        approverNamesMap[approver.status_id] = approver.user_name;
      });
      setEditingApprovers(approversMap);
      setEditingApproverNames(approverNamesMap);
    }
    setIsEditing(false);
    setAlertState(null);
  };

  const handleAssigneeSelect = (user: any) => {
    // UserSearchModal with userObjectRet=true returns the full user object
    const userId = user.id;
    const userName = user.full_name || 'Unknown';
    setEditingAssigneeId(userId);
    setEditingAssigneeName(userName);
    setAssigneeModalOpen(false);
  };

  const handleApproverSelect = (user: any) => {
    // UserSearchModal with userObjectRet=true returns the full user object
    if (editingApproverStatusId) {
      const userId = user.id;
      const userName = user.full_name || 'Unknown';
      setEditingApprovers((prev) => ({
        ...prev,
        [editingApproverStatusId]: userId,
      }));
      setEditingApproverNames((prev) => ({
        ...prev,
        [editingApproverStatusId]: userName,
      }));
    }
    setApproverModalOpen(false);
    setEditingApproverStatusId('');
  };

  const handleOpenApproverModal = (statusId: string) => {
    setEditingApproverStatusId(statusId);
    setApproverModalOpen(true);
  };

  const { mutate: updateForm, isPending: isUpdatingForm } = useApiSend(
    (params: { formId: string; data: any }) => {
      return updateSPDRosterForm(params.formId, params.data);
    },
    () => {
      // On success: show alert, wait 2 seconds, then refetch
      setAlertState({
        severity: 'success',
        description: 'Form updated successfully!',
        title: 'Success',
      });
      setIsEditing(false);
      setTimeout(() => {
        refetch();
        setAlertState(null);
      }, 2000);
    },
    (error) => {
      console.error('Failed to update form:', error);
      setAlertState({
        severity: 'error',
        description: 'Failed to update form. Please try again.',
        title: 'Error',
      });
    },
    [['spd-roster-dues-detail', id], ['roster-dues-list']]
  );

  const handleSaveEdit = () => {
    if (!id || !detail) return;

    // Build update request with only changed fields
    const updateData: any = {};

    if (editingStatusId !== detail.status_id) {
      updateData.status_id = editingStatusId;
    }

    if (editingAssigneeId !== detail.assigned_to_id) {
      updateData.assignee_id = editingAssigneeId;
    }

    // Check for approver changes
    const approverUpdates: Array<{
      status_id: string;
      approver_id: string | null;
    }> = [];
    const eligibleStatusesForUpdate = spd_roster_dues_statuses.filter(
      (s) => s.id !== SPD_COMPLETED_STATUS_ID // Exclude "Completed"
    );

    eligibleStatusesForUpdate.forEach((status) => {
      const currentApprover = detail.approvers.find(
        (a) => a.status_id === status.id
      );
      const currentApproverId = currentApprover?.user_id || null;
      const newApproverId = editingApprovers[status.id] || null;

      if (currentApproverId !== newApproverId) {
        approverUpdates.push({
          status_id: status.id,
          approver_id: newApproverId,
        });
      }
    });

    if (approverUpdates.length > 0) {
      updateData.approvers = approverUpdates;
    }

    // Only send request if there are changes
    if (Object.keys(updateData).length > 0) {
      updateForm({ formId: id, data: updateData });
    } else {
      // No changes, just exit edit mode
      setIsEditing(false);
    }
  };

  // Get eligible statuses for editing (excluding "Completed")
  const eligibleStatuses = spd_roster_dues_statuses
    .filter((s) => s.id !== SPD_COMPLETED_STATUS_ID)
    .sort((a, b) => a.order - b.order);

  // Get approver name for a status
  const getApproverName = (statusId: string): string => {
    if (isEditing) {
      const approverId = editingApprovers[statusId];
      if (!approverId) return 'Not assigned';
      // Use stored name if available, otherwise try to find in current approvers
      if (editingApproverNames[statusId]) {
        return editingApproverNames[statusId];
      }
      const approver = detail?.approvers.find((a) => a.user_id === approverId);
      return approver?.user_name || 'Not assigned';
    } else {
      const approver = detail?.approvers.find((a) => a.status_id === statusId);
      return approver?.user_name || 'Not assigned';
    }
  };

  // Get assignee name
  const getAssigneeName = (): string => {
    if (isEditing) {
      if (editingAssigneeName) {
        return editingAssigneeName;
      }
      return detail?.assigned_to || 'Not assigned';
    }
    return detail?.assigned_to || 'Not assigned';
  };

  const VirtuosoTableComponents = {
    Scroller: React.forwardRef<HTMLDivElement>((props, ref) => (
      <TableContainer
        component={Paper}
        {...props}
        ref={ref}
        sx={{ width: '100%' }}
      />
    )),
    Table: (props: any) => (
      <Table
        size="small"
        {...props}
        sx={{ borderCollapse: 'separate', tableLayout: 'fixed', width: '100%' }}
      />
    ),
    TableHead: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
      <TableHead {...props} ref={ref} />
    )),
    TableRow: TableRow,
    TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
      <TableBody {...props} ref={ref} />
    )),
  };

  const fixedHeaderContent = () => (
    <TableRow>
      <TableCell
        variant="head"
        style={{ width: '50%', minWidth: '50%' }}
        sx={{
          backgroundColor: 'background.paper',
          fontWeight: 'bold',
        }}
      >
        Chapter Name
      </TableCell>
      <TableCell
        variant="head"
        style={{ width: '30%', minWidth: '30%' }}
        sx={{
          backgroundColor: 'background.paper',
          fontWeight: 'bold',
        }}
      >
        Status
      </TableCell>
      <TableCell
        variant="head"
        style={{ width: '20%', minWidth: '20%' }}
        sx={{
          backgroundColor: 'background.paper',
          fontWeight: 'bold',
        }}
      >
        Assigned To
      </TableCell>
    </TableRow>
  );

  const rowContent = (_index: number, chapter: LocalChapterFormInfo) => (
    <React.Fragment>
      <TableCell>
        <Link
          href={`/annual-reports/roster-dues/${chapter.id}/members`}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/annual-reports/roster-dues/${chapter.id}/members`);
          }}
          sx={{ cursor: 'pointer', textDecoration: 'underline' }}
        >
          {chapter.local_chapter_name}
        </Link>
      </TableCell>
      <TableCell>
        <Chip
          label={chapter.status_name}
          size="small"
          color={
            chapter.status_id === COMPLETED_STATUS_ID
              ? 'success'
              : chapter.status_id === '4dd1112995f74575834ad8f337092a90'
                ? 'primary'
                : 'default'
          }
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {chapter.assigned_to_name || '-'}
        </Typography>
      </TableCell>
    </React.Fragment>
  );

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      {alertState && (
        <Grid item xs={12} padding={2}>
          <AlertItem
            severity={alertState.severity}
            description={alertState.description}
            title={alertState.title}
            onClose={() => setAlertState(null)}
          />
        </Grid>
      )}

      <Grid item xs={12} padding={1}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h4" color={'primary'}>
            {detail?.spd_chapter_name || 'SPD Roster & Dues Form'}
          </Typography>
          {hasEditPermission && !isEditing && (
            <IconButton
              onClick={handleEditClick}
              color="primary"
              size="small"
              sx={{ ml: 1 }}
            >
              <EditIcon />
            </IconButton>
          )}
        </Box>
        {isEditing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            <FormControl size="small" sx={{ minWidth: 250 }}>
              <InputLabel id="status-select-label">Status</InputLabel>
              <Select
                labelId="status-select-label"
                id="status-select"
                value={editingStatusId}
                label="Status"
                onChange={(e) => setEditingStatusId(e.target.value)}
              >
                {eligibleStatuses.map((status) => (
                  <MenuItem key={status.id} value={status.id}>
                    {status.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setAssigneeModalOpen(true)}
            >
              Assignee: {getAssigneeName()}
            </Button>
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Status:</strong> {detail?.status_name || 'Loading...'}
            </Typography>
            {detail?.assigned_to && (
              <Typography variant="body1">
                <strong>Assigned to:</strong> {detail.assigned_to}
              </Typography>
            )}
          </Box>
        )}
      </Grid>

      {/* Approvers Section - Only show when hasEditPermission is true */}
      {hasEditPermission && (
        <Grid item xs={12} padding={2}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Approvers
            </Typography>
            {eligibleStatuses.map((status) => (
              <Box key={status.id} sx={{ mb: 1.5 }}>
                {isEditing ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" sx={{ minWidth: 200 }}>
                      {status.name}:
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleOpenApproverModal(status.id)}
                    >
                      {getApproverName(status.id)}
                    </Button>
                  </Box>
                ) : (
                  <Typography variant="body2">
                    {status.name}: {getApproverName(status.id)}
                  </Typography>
                )}
              </Box>
            ))}
          </Paper>
        </Grid>
      )}

      {/* Edit Mode Action Buttons */}
      {isEditing && (
        <Grid item xs={12} padding={2}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleCancelEdit}
              disabled={isUpdatingForm}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveEdit}
              disabled={isUpdatingForm}
              startIcon={isUpdatingForm ? <CircularProgress size={16} /> : null}
            >
              {isUpdatingForm ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Grid>
      )}

      {!isLoading && detail && !canEdit && (
        <Grid item xs={12} padding={2}>
          <Alert severity="info">
            You are viewing this form in read-only mode. Only the assigned user
            can make changes.
          </Alert>
        </Grid>
      )}

      {!allCompleted && chapters.length > 0 && (
        <Grid item xs={12} padding={2}>
          <Alert severity="warning">
            Not all chapters are completed. Please ensure all chapters have
            status "Local Chapter Completed" before continuing to the Roster and
            Dues Form page.
          </Alert>
        </Grid>
      )}

      {verificationError && (
        <Grid item xs={12} padding={2}>
          <Alert severity="error">{verificationError}</Alert>
        </Grid>
      )}

      {/* Progress Indicator */}
      {chapters.length > 0 && (
        <Grid item xs={12} padding={2}>
          <Typography variant="body1" fontWeight="medium">
            Progress: {completedChapters.length} of {chapters.length} chapters
            completed
          </Typography>
        </Grid>
      )}

      {/* Search */}
      <Grid item xs={12} sm={6} md={4} paddingBottom={2}>
        <TextField
          label="Search Chapters"
          id="search-chapters"
          size="small"
          value={delaySearch}
          fullWidth
          onChange={(e) => setDelaySearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      {isLoading ? (
        <Grid item xs={12} textAlign="center" padding={4}>
          <CircularProgress />
        </Grid>
      ) : (
        <>
          {groupedChapters.map((group) => {
            return (
              <Grid item xs={12} key={group.status.id} sx={{ mb: 2 }}>
                <Accordion
                  expanded={group.isExpanded}
                  onChange={() => handleToggleStatus(group.status.id)}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        width: '100%',
                      }}
                    >
                      <Typography variant="h6">
                        {group.status.name} ({group.chapters.length})
                      </Typography>
                      {group.status.id === COMPLETED_STATUS_ID && (
                        <Chip label="Completed" size="small" color="success" />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0, overflow: 'hidden' }}>
                    {group.chapters.length > 0 ? (
                      <Box
                        sx={{
                          height: Math.min(
                            group.chapters.length * 53 + 60,
                            600
                          ),
                          width: '100%',
                          overflow: 'hidden',
                        }}
                      >
                        <TableVirtuoso
                          data={group.chapters}
                          components={VirtuosoTableComponents}
                          fixedHeaderContent={fixedHeaderContent}
                          itemContent={rowContent}
                          style={{ width: '100%' }}
                        />
                      </Box>
                    ) : (
                      <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          No chapters in this status
                        </Typography>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Grid>
            );
          })}

          {chapters.length === 0 && !isLoading && (
            <Grid item xs={12} padding={4} textAlign="center">
              <Typography variant="body1">No chapters found.</Typography>
            </Grid>
          )}
        </>
      )}

      <Grid item xs={12} paddingTop={4} paddingBottom={2}>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/annual-reports/roster-dues/spd')}
            disabled={isVerifying || isEditing}
          >
            Back to List
          </Button>
          <Button
            variant="contained"
            onClick={handleContinueToSummary}
            disabled={!allCompleted || isVerifying || isEditing}
          >
            {isVerifying ? 'Verifying...' : 'Continue to Roster And Dues Form'}
          </Button>
        </Box>
      </Grid>

      {/* User Search Modals */}
      <UserSearchModal
        modalOpen={assigneeModalOpen}
        setModalOpen={setAssigneeModalOpen}
        setUser={handleAssigneeSelect}
        chapter={null}
        spd={null}
        activeMembers={false}
        userObjectRet={true}
        allowSetToMe={true}
      />
      <UserSearchModal
        modalOpen={approverModalOpen}
        setModalOpen={setApproverModalOpen}
        setUser={handleApproverSelect}
        chapter={null}
        spd={null}
        activeMembers={false}
        userObjectRet={true}
        allowSetToMe={true}
      />
    </Grid>
  );
};

export default RosterDuesChapterList;
